import * as React from 'react';
import {
  AccessType,
  BuildJsonType,
  IConfirmationInstructions,
} from '@bb/domain/dist/types';

import SupraAccess from './SupraAccess';

type InstructionRowProps = {
  label: string;
  value: React.ReactNode;
};

const InstructionRow: React.FC<InstructionRowProps> = ({ label, value }) => (
  <tr>
    <td className="table-key">{label}</td>
    <td>{value}</td>
  </tr>
);

type OtherInstructions =
  | 'lights'
  | 'shoes'
  | 'card'
  | 'lock_doors'
  | 'knockFirst'
  | 'callIfLate'
  | 'reco';

const otherInstructions: OtherInstructions[] = [
  'callIfLate',
  'reco',
  'lights',
  'shoes',
  'card',
  'knockFirst',
  'lock_doors',
];

type AppointmentInstructionsProps = {
  dynamicLockBoxCode?: string;
  accessType?: AccessType;
  instructions?: BuildJsonType<IConfirmationInstructions>;
};

const AppointmentInstructions: React.FunctionComponent<
  AppointmentInstructionsProps
> = ({ dynamicLockBoxCode, accessType, instructions = {} }) => {
  const instructionsData = [
    accessType === 'Lock Box' &&
      instructions.lockbox?.code && {
        label: 'Lock Box Code',
        value: instructions.lockbox.code,
      },
    accessType === 'SentriLockV2' &&
      instructions.sentrilockv2?.type === 'ADD_APPOINTMENT' && {
        label: 'SentriLock Access',
        value: 'SentriKey App',
      },
    accessType === 'SentriLockV2' &&
      instructions.sentrilockv2?.type === 'SENTRI_CONNECT' && {
        label: 'SentriLock Access',
        value: 'SentriConnect App',
      },
    accessType === 'SentriLockV2' &&
      instructions.sentrilockv2?.type === 'UNAUTHORIZED' &&
      (instructions.sentrilockv2?.isOneDayCodeEnabled
        ? {
            label: 'SentriLock Access',
            value: 'SentriLock 1-Day Code',
          }
        : {
            label: 'SentriLock Access',
            value: 'SentriLock App - Contact listing agent for access',
          }),
    ['SentriLock', 'SentriLockV2', 'MasterLock'].includes(accessType) &&
      dynamicLockBoxCode && {
        label: 'Lock Box Code',
        value: dynamicLockBoxCode,
      },
    accessType === 'Lock Box' &&
      instructions.lockbox?.details && {
        label: 'Lock Box Instructions',
        value: instructions.lockbox.details,
      },
    accessType === 'Door Code' &&
      instructions.doorcode?.code && {
        label: 'Door Code',
        value: instructions.doorcode.code,
      },
    accessType === 'Door Code' &&
      instructions.doorcode?.details && {
        label: 'Door Code Instructions',
        value: instructions.doorcode.details,
      },
    accessType === 'SentriLock' &&
      instructions.sentrilock?.details && {
        label: 'SentriLock Instructions',
        value: instructions.sentrilock.details,
      },
    accessType === 'SentriLockV2' &&
      instructions.sentrilockv2?.details && {
        label: 'SentriLock Instructions',
        value: instructions.sentrilockv2.details,
      },
    accessType === 'SentriLock App' &&
      instructions.sentrilockapp?.details && {
        label: 'SentriLock App Instructions',
        value: instructions.sentrilockapp.details,
      },
    accessType === 'Supra eKEY' && {
      label: 'Access',
      value: <SupraAccess />,
    },
    accessType === 'Supra eKEY' &&
      instructions.supraekey?.details && {
        label: 'Special Access Instructions',
        value: instructions.supraekey?.details,
      },
    accessType === 'Direct' &&
      instructions.direct?.details && {
        label: 'Details',
        value: instructions.direct.details,
      },
    accessType === 'Key' &&
      instructions.key?.details && {
        label: 'Key',
        value: instructions.key.details,
      },
    instructions.alarm?.code && {
      label: 'Alarm Code',
      value: instructions.alarm.code,
    },
    instructions.alarm?.details && {
      label: 'Alarm Instructions',
      value: instructions.alarm.details,
    },
    instructions.confirmation?.details && {
      label: 'Other Instructions',
      value: instructions.confirmation.details,
    },
  ].filter(Boolean);

  const otherInstructionsMap: Record<
    keyof Pick<IConfirmationInstructions, OtherInstructions>,
    string
  > = {
    callIfLate: 'Call if Late or Cancelling',
    reco: 'Bring Agent License',
    lights: 'Turn Off Lights',
    shoes: 'Remove Shoes',
    card: 'Leave Card',
    knockFirst: 'Knock First',
    lock_doors: 'Lock Doors', // eslint-disable-line
  };

  const enabledOtherInstructions = otherInstructions.filter(
    instruction => instructions[instruction],
  );

  if (!enabledOtherInstructions.length) return null;

  return (
    <>
      <label>Instructions:</label>
      <table className="table" style={{ width: '100%' }}>
        <tbody>
          {instructionsData.map((data, index) => (
            <InstructionRow key={index} {...data} />
          ))}

          {enabledOtherInstructions.map((instruction, index) => (
            <InstructionRow
              key={index}
              label={index === 0 ? 'Other' : ''}
              value={otherInstructionsMap[instruction]}
            />
          ))}
        </tbody>
      </table>
    </>
  );
};

export default AppointmentInstructions;
